import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Global confirm / prompt modal — replaces native window.confirm / window.prompt
 * with styled, non-native dialogs. Imperative API:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title, message, danger: true })) { ... }
 *
 *   const promptModal = usePrompt();
 *   const value = await promptModal({ title, message, placeholder });  // string | null
 *
 * Mounted once at the app root (see App.jsx).
 */
const ConfirmContext = createContext(null);

export function ConfirmModalProvider({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [value, setValue] = useState('');
  const resolverRef = useRef(null);

  const open = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setValue(opts.defaultValue || '');
      setState(opts);
    });
  }, []);

  const close = useCallback((result) => {
    setState(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((opts = {}) => open({ ...opts, type: 'confirm' }), [open]);
  const prompt = useCallback((opts = {}) => open({ ...opts, type: 'prompt' }), [open]);

  const isPrompt = state?.type === 'prompt';
  // Two « Annuler » of opposite meaning (« Annuler » / « Annuler la réservation », QA 31/10 #21): when the action
  // itself starts with « Cancel », the button that closes says « Go back » instead.
  const cancelWord = t('Cancel');
  const cancelLabel = state?.cancelText
    || (String(state?.confirmText || '').toLowerCase().startsWith(String(cancelWord).toLowerCase()) ? t('Go back') : cancelWord);
  // Backdrop / Escape / Cancel → false for confirm, null for prompt.
  const cancelResult = isPrompt ? null : false;
  const acceptResult = isPrompt ? value : true;

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => close(cancelResult)}
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {state.title && (
              <h3 className="mb-2 text-lg font-semibold text-gray-900">{state.title}</h3>
            )}
            {state.message && (
              <p className="mb-4 whitespace-pre-line text-sm text-gray-600">{state.message}</p>
            )}
            {isPrompt && (
              <input
                autoFocus
                type="text"
                value={value}
                placeholder={state.placeholder || ''}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') close(value);
                  if (e.key === 'Escape') close(null);
                }}
                className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {/* hideCancel: an information to acknowledge, with one button */}
              {!state.hideCancel && (
              <button
                type="button"
                onClick={() => close(cancelResult)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
              >
                {cancelLabel}
              </button>
              )}
              <button
                type="button"
                autoFocus={!isPrompt}
                onClick={() => close(acceptResult)}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition ${
                  state.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {state.confirmText || t('Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

function useConfirmContext() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm/usePrompt must be used within <ConfirmModalProvider>');
  return ctx;
}

export const useConfirm = () => useConfirmContext().confirm;
export const usePrompt = () => useConfirmContext().prompt;
